import { angleDelta, clamp, lerp, normalizeAngle } from "../game/math";
import type {
  DeepReadonly,
  MatchSnapshot,
  ReadonlyMatchSnapshot,
  SnakeState,
} from "../game/types";
import { interpolateLengthCurve } from "../game/tuning";

const CAMERA_SCALE_CURVE = [
  { length: 160, value: 1.12 },
  { length: 320, value: 0.98 },
  { length: 640, value: 0.82 },
  { length: 1_200, value: 0.68 },
  { length: 1_800, value: 0.6 },
] as const;

export const cameraScaleForLength = (length: number): number =>
  interpolateLengthCurve(length, CAMERA_SCALE_CURVE);

const interpolateSnake = (
  previous: DeepReadonly<SnakeState>,
  current: DeepReadonly<SnakeState>,
  alpha: number,
): SnakeState => {
  const position = lerp(previous.position, current.position, alpha);
  const points = current.trail.points;
  const trail =
    points.length === 0
      ? current.trail
      : { points: [...points.slice(0, -1), position] };

  return {
    ...current,
    position,
    heading: normalizeAngle(
      previous.heading + angleDelta(previous.heading, current.heading) * alpha,
    ),
    trail,
  };
};

/**
 * Builds a visual snapshot between two authoritative simulation states.
 * Fields that do not affect positional continuity always come from `current`.
 */
export const interpolateMatchSnapshot = (
  previous: ReadonlyMatchSnapshot,
  current: ReadonlyMatchSnapshot,
  alpha: number,
): MatchSnapshot => {
  const amount = clamp(Number.isNaN(alpha) ? 0 : alpha, 0, 1);
  const previousById = new Map(
    previous.snakes.map((snake) => [snake.id, snake] as const),
  );

  return {
    ...current,
    snakes: current.snakes.map((currentSnake) => {
      const previousSnake = previousById.get(currentSnake.id);
      return previousSnake &&
        (previousSnake.spawnGeneration ?? 0) ===
          (currentSnake.spawnGeneration ?? 0)
        ? interpolateSnake(previousSnake, currentSnake, amount)
        : currentSnake;
    }),
  };
};

/**
 * Converts a legacy per-frame smoothing factor into a delta-time-based factor.
 */
export const cameraSmoothingFactor = (
  renderDeltaSeconds: number,
  reducedMotion: boolean,
): number => {
  if (reducedMotion) return 1;
  if (renderDeltaSeconds <= 0 || Number.isNaN(renderDeltaSeconds)) return 0;
  if (renderDeltaSeconds === Number.POSITIVE_INFINITY) return 1;

  return 1 - Math.pow(1 - 0.14, renderDeltaSeconds * 60);
};
